import React, { FC, memo, useState } from 'react';
import { useIntl } from 'umi';
import { Bubble, Icon } from 'tea-component';
import { LANGUAGE_EN } from '@home/_config/config';
import BasicImage from '@home/components/BasicImage';
import closeIcon from '@/pages/aigc/assets/svg/skill-close.svg';
import closeActiveIcon from '@/pages/aigc/assets/svg/agent-skill-del-active.svg';
import SkillsDefaultIcon from '@/pages/aigc/assets/svg/skills-default-icon.svg';

// ==================== 类型 ====================

export interface ISelectedSkillTagData {
  /** Skill 唯一 ID */
  skillId: string;
  /**
   * 技能包名，与 ISkillData.skill_name / specified_skills.name 一致（唯一真相来源）；
   * 无 display_name_* 时亦作标签展示兜底
   */
  name: string;
  /** 中文展示名 */
  display_name_cn?: string;
  /** 英文展示名 */
  display_name_en?: string;
  /** 图标地址 */
  icon: string;
  /** 类型 */
  type?: string;
  /** 显示名 */
  displayName: string;
  /** 是否处于 loading 占位态（数据未返回时） */
  isLoading?: boolean;
}

/**
 * 按当前界面语言解析标签展示文案
 */
export function resolveSelectedSkillDisplayName(
  skill: ISelectedSkillTagData,
  isEN: boolean,
): string {
  return isEN
    ? (skill.display_name_en?.trim() || skill.name || skill.displayName)
    : (skill.display_name_cn?.trim() || skill.name || skill.displayName);
}

interface ISelectedSkillTagProps {
  /** 选中的 Skill 数据 */
  skill: ISelectedSkillTagData;
  /** 关闭（移除）回调 */
  onClose: (skillId: string) => void;
}

// ==================== 组件实现 ====================

/** BEM 前缀 */
const PREFIX = 'dba-selected-skill-tag';

/**
 * 选中 Skill 标签组件
 * 设计稿样式：图标 + 名称 + x 关闭按钮（在同一行内）
 */
const SelectedSkillTag: FC<ISelectedSkillTagProps> = ({ skill, onClose }) => {
  const { locale } = useIntl();
  const isEN = locale === LANGUAGE_EN;
  const label = resolveSelectedSkillDisplayName(skill, isEN);
  const [isCloseHovered, setIsCloseHovered] = useState<boolean>(false);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onClose(skill.skillId);
  };

  /**
   * 阻止鼠标点击在标签内部插入光标
   * 防止光标进入该元素的可编辑区域
   */
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const tipContent = isEN
    ? (<>Selected skills will be used in this response;<br />unselected skills won&apos;t be applied.</>)
    : (<>已选中的skill会参与本次回答，<br />未选中的不参与智能调用。</>);

  const bubbleOverlayClassName = 'dba-nav-menu-overlay dba-skill-tag-tip';

  const tagBody = (
    <>
      {skill.isLoading ? (
        <span className={`${PREFIX}__icon`}>
          <Icon type="loading" />
        </span>
      ) : (
        <BasicImage
          src={skill.icon}
          alt={label}
          errorSrc={SkillsDefaultIcon}
          className={`${PREFIX}__icon`}
        />
      )}
      <div
        className={`${PREFIX}__close`}
        onClick={handleClose}
        onMouseEnter={() => setIsCloseHovered(true)}
        onMouseLeave={() => setIsCloseHovered(false)}
      >
        <img
          src={isCloseHovered ? closeActiveIcon : closeIcon}
          alt="close"
          className={`${PREFIX}__close-icon`}
        />
      </div>
      <span className={`${PREFIX}__name`}>{label}</span>
    </>
  );

  return (
    <Bubble dark placement="top" content={tipContent} overlayClassName={bubbleOverlayClassName}>
      <div
        className={PREFIX}
        contentEditable={false}
        suppressContentEditableWarning
        onMouseDown={handleMouseDown}
      >
        {tagBody}
      </div>
    </Bubble>
  );
};

export default memo(SelectedSkillTag);
